import { connect } from 'react-redux';
import { createStructuredSelector, createSelector } from 'reselect';

import actions from 'Actions';
import LoveActions from 'actions/LoveActions';
import skuToEditSelector from 'selectors/loves/skuToEditSelector';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import FrameworkUtils from 'utils/framework';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import swatchUtils from 'utils/Swatch';
import skuUtils from 'utils/Sku';
import myListsUtils from 'utils/MyLists';
import EditMyListProductModalEN from 'components/GlobalModals/EditMyListProductModal/locales/EditMyListProductModal_en_US';
import EditMyListProductModalFR from 'components/GlobalModals/EditMyListProductModal/locales/EditMyListProductModal_fr_CA';
const { createRefinementGroups, buildSwatchGroupsAndFilters, SWATCH_GROUP_VIEWS, REFINEMENT_CONSTANTS } = swatchUtils;

const { wrapHOC } = FrameworkUtils;
const { getLocalisedDataFromResource, getTextFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(EditMyListProductModalEN, EditMyListProductModalFR);

const localization = createStructuredSelector({
    color: getTextFromResource(getText, 'color'),
    size: getTextFromResource(getText, 'size'),
    edit: getTextFromResource(getText, 'edit'),
    type: getTextFromResource(getText, 'type'),
    scent: getTextFromResource(getText, 'scent'),
    sizeConcentration: getTextFromResource(getText, 'sizeConcentration'),
    save: getTextFromResource(getText, 'save'),
    cancel: getTextFromResource(getText, 'cancel'),
    noVariants: getTextFromResource(getText, 'noVariants'),
    gotIt: getTextFromResource(getText, 'gotIt'),
    genericErrorMessage: getTextFromResource(getText, 'genericErrorMessage'),
    changesNotSaved: getTextFromResource(getText, 'changesNotSaved'),
    duplicateItem: getTextFromResource(getText, 'duplicateItem')
});

const fields = createSelector(skuToEditSelector, localization, (skuToEdit, textResources) => {
    let refinementGroups;
    let swatchGroupsAndFilters;

    if (skuToEdit) {
        refinementGroups = createRefinementGroups({
            regularChildSkus: [...(skuToEdit?.regularChildSkus || []), ...(skuToEdit?.onSaleChildSkus || [])]
        });
        swatchGroupsAndFilters = buildSwatchGroupsAndFilters(refinementGroups);
    }

    const isSizeVariation = skuToEdit && skuToEdit.variationType?.toLowerCase().includes(REFINEMENT_CONSTANTS.SIZE);
    const variationType = myListsUtils.fromScreamingUpperCaseToCamelCase(skuToEdit?.variationType || '');

    const swatchGroupView =
        skuToEdit && skuToEdit.skuSelectorType === skuUtils.skuSwatchType.IMAGE ? SWATCH_GROUP_VIEWS.LIST : SWATCH_GROUP_VIEWS.GRID;

    const childSkus = skuToEdit && (skuToEdit.regularChildSkus || skuToEdit.onSaleChildSkus);
    const productHasVariants = childSkus && childSkus.length > 1;

    return {
        skuToEdit,
        variationType,
        swatchGroupView,
        swatchGroupsAndFilters,
        productHasVariants,
        isSizeVariation,
        localization: {
            ...textResources
        }
    };
});

const functions = (dispatch, ownProps) => ({
    showEditMyListProductModal: ({ isOpen }) => {
        const action = actions.showEditMyListProductModal({ isOpen });
        dispatch(action);
    },
    setSkuLoveData: skuLoveData => {
        const action = LoveActions.setSkuLoveData(skuLoveData);
        dispatch(action);
    },
    getAllLists: ({ callback, force, options }) => {
        const data = LoveActions.getAllLists({ callback, force, options });

        dispatch(data);
    },
    setLoveListUpdated: isUpdated => {
        dispatch(LoveActions.setLoveListUpdated(isUpdated));
    },
    getLimitedLoveListItems: ({ callback, refreshCache, options }) => {
        const action = LoveActions.getLimitedLoveListItems({ callback, force: refreshCache, options });
        dispatch(action);
    },
    getFlatLoveListSkusWithDetails: (callback, refreshCache, options) => {
        const action = LoveActions.getFlatLoveListSkusWithDetails({ callback: () => {}, force: refreshCache, options });
        dispatch(action);
    },
    updateReduxLoveList: async () => {
        await Storage.db.removeItem(LOCAL_STORAGE.ALL_LOVE_LIST_SKU_ONLY);
        const lovesFromIndexedDB = await Storage.db.getItem(LOCAL_STORAGE.LOVED_ITEMS_SKU_ONLY, false, false, true);
        const loves = Array.isArray(lovesFromIndexedDB.data.skus) ? lovesFromIndexedDB.data.skus : [];
        const totalLovesListItemsCount = lovesFromIndexedDB.data.totalSkus ?? loves.length;

        const loveIds = LoveActions.setShoppingListIds(loves);
        const skuProductIds = LoveActions.setLoveListSkuProductIds(loves);

        dispatch(LoveActions.updateShoppingListIds(loveIds));
        dispatch(LoveActions.updateLoveListSkuProductSet(skuProductIds));
        dispatch(
            LoveActions.updateLovesListReduxOnly({
                loves,
                totalLovesListItemsCount,
                shareLink: lovesFromIndexedDB.data.shareLink
            })
        );
    },
    saveReplaceItem: async ({ skuToSave, callback, cbCleanup }) => {
        const { shoppingListItemId } = ownProps;
        const action = LoveActions.saveReplaceItem({ skuToSave, shoppingListItemId, callback, cbCleanup });
        await dispatch(action);
    },
    showInfoModal: ({ title, message, buttonText }) => {
        const action = actions.showInfoModal({
            isOpen: true,
            title,
            message,
            buttonText
        });
        dispatch(action);
    }
});

const withEditMyListProductModalProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, withEditMyListProductModalProps
};
